// react
import { useCallback, useEffect, useMemo, useState } from 'react';
// react-router-dom
import { useSearchParams } from 'react-router-dom';
// @mui
import {
  Backdrop,
  Box,
  Card,
  Chip,
  CircularProgress,
  Link,
  ListItem,
  Pagination,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

// components
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// routes
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';
// APIs
import { findBooks } from 'src/api/public/searchBook';
// utils
import Image from 'src/components/image';
import { DOCUMENT_HOST_API, IMAGE_HOST_API } from 'src/config-global';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import TextMaxLine from 'src/components/text-max-line';
import { IBookPublic } from 'src/types/DTO/public/IBookPublic';
import GuestToolbox from '../_components/guest-toolbox';
import RegisterBorrowBookForm from '../_components/register-borrow-book-form/register-borrow-book-form';
import useBasket from '../_hooks/useBasket';
import {
  ButtonAddToBasket,
  ButtonBorrowRegister,
  ButtonRemoveFromBasket,
} from '../_components/public-buttons';
import BookCarousel from './components/bookCarousel';

const TABS = [
  {
    value: 1,
    label: 'Tổng quan',
  },
  {
    value: 2,
    label: 'Vị trí lưu trữ',
  },
  {
    value: 3,
    label: 'Liên kết',
  },
];

export default function TraCuuAnPham() {
  const { bookInStorage, setBaskets, removeFromBasket, toBasket } = useBasket();
  const [result, setResult] = useState<any>([]);
  const [page, setPage] = useState(1);
  const isLoading = useBoolean();
  const [pagination, setPagination] = useState<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabManager, setTabManager] = useState<{ value: number }[]>([]);
  const [recommendedBook, setRecommendedBook] = useState<any>([]);
  const [bookBorrow, setBookBorrow] = useState<any>(null);

  useEffect(() => {
    setBaskets(bookInStorage.map((book: any) => ({ id: book })));
  }, [bookInStorage]);

  useEffect(() => {
    if (searchParams.size > 0) fetch();
    else {
      setResult([]);
      setPage(1);
      setPagination(null);
    }
    async function fetch() {
      try {
        isLoading.onTrue();

        // setSearchParams(params);
        const response = await findBooks({
          limit: 15,
          page,
          ...convertParams(searchParams),
        });
        if (response.data.list) {
          setResult([...response.data.list]);
          setPagination(response.data.pagination);
          setTabManager(Array.from({ length: response.data.list.length }, () => ({ value: 1 })));
        }
        isLoading.onFalse();
      } catch (error) {
        console.error(error);
        isLoading.onFalse();
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchBooks = async () => {
      const fBook = await findBooks();
      setRecommendedBook(fBook.data.list.slice(0, 5));
    };

    fetchBooks();
  }, [findBooks]);

  const totalCountReady = useMemo(() => {
    let totals = [];
    if (result.length > 0) {
      totals = result.map((item: any) =>
        item.manageInfor.reduce((total: any, infor: any) => {
          if (infor.isReady) return total + 1;
          return total;
        }, 0)
      );
    }
    return totals;
  }, [result]);

  useEffect(() => {
    return () => {
      setPage(1);
      setResult([]);
      setPagination(null);
    };
  }, []);

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent, newValue: number, index: number) => {
      // console.log('before', tabManager);
      const newTab = [...tabManager];
      newTab[index] = { value: newValue };
      // console.log('after', newTab);
      setTabManager(newTab);
    },
    [tabManager]
  );

  const handleBorrowBook = useCallback(
    (bookBorrowId: any, bookInfor: any) => {
      setBookBorrow({ ids: [bookBorrowId], infors: [bookInfor] });
    },
    [setBookBorrow]
  );

  const tab1 = (item: IBookPublic, index: number) => (
    <Stack direction="column" alignItems="flex-start" spacing={0.5}>
      <Link
        variant="h4"
        display="flex"
        alignItems="center"
        gap={0.5}
        href={paths.guest.chi_tiet_sach(item.slug)}
        component={RouterLink}
      >
        <TextMaxLine>{item.name}</TextMaxLine>
        <Iconify icon="fxemoji:openbook" sx={{ transform: 'rotate(10deg)' }} />
      </Link>
      <Typography variant="subtitle2">{item.author}</Typography>

      <Typography variant="caption">
        Hiện có <strong>{totalCountReady[index] || 0}</strong>/{item.manageInfor.length} ấn phẩm tại
        thư viện
      </Typography>
      <Stack direction="row" sx={{ overflowX: 'auto' }} spacing={0.5}>
        <Chip label={item.categoryName} size="small" variant="outlined" />
        <Chip label={item.language} size="small" variant="outlined" />
      </Stack>
    </Stack>
  );
  const tab2 = (item: IBookPublic, index: number) => (
    <Box sx={{ flex: 1, position: 'relative' }}>
      {item?.manageInfor?.length === 0 && (
        <EmptyContent
          title="Ấn phẩm này chưa có vị trí lưu trữ !"
          imgWidth={80}
          titleSx={{ typography: 'caption', fontWeight: 'bold' }}
        />
      )}
      {item?.manageInfor?.length > 0 && (
        <>
          <Typography variant="body2" mb={0.5}>
            Trong kho sẵn sàng cho mượn: <strong>{totalCountReady[index] || 0}</strong>
          </Typography>
          <Scrollbar>
            <Box width="fit-content">
              <DataGrid
                rows={item?.manageInfor?.map((infor) => ({
                  id: infor.bookId,
                  bookCode: infor.bookCode,
                  positionName: infor.positionName,
                  isReady: infor.isReady,
                  actions: infor.bookId,
                }))}
                columns={[
                  {
                    field: 'bookCode',
                    minWidth: 100,
                    headerName: 'Mã ấn phẩm',
                  },
                  {
                    field: 'positionName',
                    minWidth: 150,
                    headerName: 'Vị trí lưu trữ',
                  },
                  {
                    field: 'isReady',
                    minWidth: 100,
                    headerName: 'Tình trạng',
                    align: 'center',
                    headerAlign: 'center',
                    renderCell: (params) => (
                      <Label color={params.row.isReady ? 'success' : 'error'}>
                        {params.row.isReady ? 'Sẵn sàng' : 'Không có sẵn'}
                      </Label>
                    ),
                  },
                  {
                    field: 'actions',
                    minWidth: 150,
                    headerName: 'Hành động',
                    headerAlign: 'center',
                    align: 'center',
                    renderCell: (params) => {
                      // console.log(params);
                      return (
                        <Stack sx={{ maxWidth: 150 }} my={0.5}>
                          {params.row.isReady && (
                            <ButtonBorrowRegister
                              onClick={() => handleBorrowBook(params.id, item)}
                            />
                          )}
                          {!bookInStorage.includes(params.id) && (
                            <ButtonAddToBasket onClick={() => toBasket(params.id)} />
                          )}
                          {bookInStorage.includes(params.id) && (
                            <ButtonRemoveFromBasket onClick={() => removeFromBasket(params.id)} />
                          )}
                        </Stack>
                      );
                    },
                  },
                ]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 5,
                    },
                  },
                }}
                pageSizeOptions={[5]}
                disableColumnMenu
                disableRowSelectionOnClick
                density="compact"
                getRowHeight={() => 'auto'}
              />
            </Box>
          </Scrollbar>
        </>
      )}
    </Box>
  );
  const tab3 = (item: IBookPublic, index: number) => (
    <Box>
      {item?.attachFiles?.length === 0 && (
        <EmptyContent
          title="Ấn phẩm này chưa có tài liệu số !"
          imgUrl="/assets/icons/empty/ic_folder_empty.svg"
          imgWidth={80}
          titleSx={{ typography: 'caption', fontWeight: 'bold' }}
        />
      )}
      {item?.attachFiles?.length > 0 && (
        <TableContainer sx={{ overflow: 'unset' }}>
          <Scrollbar sx={{ width: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">STT</TableCell>
                  <TableCell>File</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {item?.attachFiles?.map((file, ix: number) => (
                  <TableRow
                    key={file.fileName + ix}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row" align="left">
                      {ix + 1}
                    </TableCell>
                    <TableCell>
                      <Link href={DOCUMENT_HOST_API + file.fileURL} target="_blank">
                        {file.fileName}
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <>
      <GuestToolbox />
      {result?.length > 0 ? (
        <Stack
          mt={2}
          spacing={2}
          sx={{
            px: {
              xs: 0,
              md: 1,
            },
          }}>
          {result.map((row: IBookPublic, index: number) => (
            <Stack
              key={row.id}
              sx={{
                width: 1,
                position: 'relative',
                borderRadius: 0.5,
                flexDirection: {
                  xs: 'column',
                  md: 'row',
                },
                py: 2,
              }}
              p={1}
              // direction="row"
              spacing={1}
              component={Card}
              alignItems="flex-start"
            >
              <Image
                src={IMAGE_HOST_API + row.photoURL}
                ratio="1/1"
                sx={{
                  maxWidth: 150,
                  // maxHeight: 150,
                  mx: {
                    xs: 'auto',
                    md: 'unset',
                  },
                }}
              />
              <Box flexGrow={1} width={1}>
                <Tabs
                  value={tabManager[index].value}
                  onChange={(event, newValue) => handleChangeTab(event, newValue, index)}
                  sx={{ mb: 0.5 }}
                >
                  {TABS.map((tab) => {
                    return <Tab key={tab.value} value={tab.value} label={tab.label} />;
                  })}
                </Tabs>
                {tabManager[index].value === 1 && tab1(row, index)}
                {tabManager[index].value === 2 && tab2(row, index)}
                {tabManager[index].value === 3 && tab3(row, index)}
              </Box>
            </Stack>
          ))}

          <Stack>
            <ListItem sx={{ mt: 1 }}>
              <Pagination
                sx={{ mx: 'auto' }}
                count={pagination?.totalPages || 1}
                shape="rounded"
                onChange={(event: any, newPage: number) => {
                  setPage(newPage);
                  searchParams.set('page', newPage.toString());
                  setSearchParams(searchParams);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </ListItem>
          </Stack>
        </Stack>
      ) : (
        recommendedBook?.length > 0 && (
          <Stack mt={2} spacing={2} sx={{ px: { xs: 0, md: 1 } }}>
            <BookCarousel recommendedBook={recommendedBook} />
          </Stack>
        )
      )}
      <Backdrop open={isLoading.value} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}>
        <CircularProgress size={80} />
      </Backdrop>

      {(!result || result?.length === 0) && !isLoading.value && (
        <Box mt={10}>
          <EmptyContent imgUrl="/logo/iit_logo.png" title="Tra cứu ấn phẩm trong thư viện" />
        </Box>
      )}

      <RegisterBorrowBookForm
        open={!!bookBorrow}
        books={bookBorrow}
        onClose={() => setBookBorrow(null)}
      />
    </>
  );
}
function convertParams(searchParams: any) {
  // console.log('params: ', params);

  const query = searchParams.get('query');
  const fieldIds = searchParams.get('fieldIds');
  const languageIds = searchParams.get('languageIds');
  const categoryIds = searchParams.get('categoryIds');
  const page = searchParams.get('page');
  // console.log(query, fieldIds, languageIds, categoryIds);
  let returnParams = {};
  if (query) {
    returnParams = { ...returnParams, keyword: query };
  }
  if (fieldIds) {
    returnParams = { ...returnParams, fieldIds };
  }
  if (languageIds) {
    returnParams = { ...returnParams, languageIds };
  }
  if (categoryIds) {
    returnParams = { ...returnParams, categoryIds };
  }
  if (page) {
    returnParams = { ...returnParams, page };
  }
  return returnParams;
}
